import { useState, useEffect, useCallback } from 'react';
import {
  TradingFormUIState,
  defaultTradingFormUIState,
  normalizeTradingFormUIState,
  toPersistedTradingFormUIState
} from './trading-form-ui-state.types';

const UI_STATE_STORAGE_KEY = 'trading-form-ui-state';

type DeepPaths<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T]: K extends string
          ? T[K] extends Array<infer U>
            ?
                | `${K}`
                | `${K}.${number}`
                | (U extends Record<string, unknown>
                    ? `${K}.${number}.${DeepPaths<U>}`
                    : never)
            : T[K] extends Record<string, unknown>
              ? `${K}` | `${K}.${DeepPaths<T[K]>}`
              : `${K}`
          : never;
      }[keyof T]
    : never;

// Convert path string to array of keys
type PathToArray<S extends string> = S extends `${infer K}.${infer Rest}`
  ? K extends `${number}`
    ? [number, ...PathToArray<Rest>]
    : [K, ...PathToArray<Rest>]
  : S extends `${number}`
    ? [number]
    : [S];

// Extract all possible paths from TradingFormUIState
type AllPaths = PathToArray<DeepPaths<TradingFormUIState>>;

// Create type that includes all valid paths
export type UiStatePath = AllPaths;

export const useTradingFormUIState = () => {
  const [uiState, setUIState] = useState<TradingFormUIState>(
    defaultTradingFormUIState
  );
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      let restoredUiState: TradingFormUIState | undefined;
      const stored = localStorage.getItem(UI_STATE_STORAGE_KEY);
      if (stored) {
        restoredUiState = JSON.parse(stored);
      }

      if (restoredUiState) {
        setUIState(normalizeTradingFormUIState(restoredUiState));
      }
    } catch (err) {
      console.warn('Failed to load UI state:', err);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    try {
      localStorage.setItem(
        UI_STATE_STORAGE_KEY,
        JSON.stringify(toPersistedTradingFormUIState(uiState))
      );
    } catch (err) {
      console.warn('Failed to save UI state:', err);
    }
  }, [uiState, isLoaded]);

  const updateNestedValue = useCallback(
    (
      obj: Record<string, unknown> | unknown[],
      path: (string | number)[],
      value: unknown
    ): Record<string, unknown> | unknown[] => {
      if (path.length === 0) return obj;

      const [head, ...tail] = path;

      if (Array.isArray(obj)) {
        const index = Number(head);
        const cloned = obj.slice();

        if (tail.length === 0) {
          cloned[index] = value;
          return cloned;
        }

        const currentElement = (obj[index] as Record<string, unknown>) ?? {};
        cloned[index] = updateNestedValue(currentElement, tail, value);
        return cloned;
      }

      if (tail.length === 0) {
        return {
          ...obj,
          [head]: value
        };
      }

      const nextHead = tail[0];
      const currentValue =
        (obj[head as string] as Record<string, unknown>) ??
        (typeof nextHead === 'number' ? [] : {});
      return {
        ...obj,
        [head]: updateNestedValue(currentValue, tail, value)
      };
    },
    []
  );

  const setUiState = useCallback(
    <T>(path: UiStatePath, value: T) => {
      setUIState((prev) =>
        normalizeTradingFormUIState(
          updateNestedValue(
            prev as Record<string, unknown>,
            path as (string | number)[],
            value
          ) as TradingFormUIState
        )
      );
    },
    [updateNestedValue]
  );

  const getUiState = useCallback(
    <T>(path: UiStatePath, fallback: T): T => {
      let node: unknown = uiState;
      for (const key of path) {
        if (node == null || typeof node !== 'object') {
          return fallback;
        }
        node = (node as Record<string, unknown>)[key];
      }
      return (node as T) ?? fallback;
    },
    [uiState]
  );

  const resetUIState = useCallback(() => {
    setUIState(defaultTradingFormUIState);
  }, []);

  return {
    uiState,
    setUiState,
    getUiState,
    resetUIState,
    isLoaded
  };
};
